import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { shallowEqual, useSelector } from 'umi';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import MetricsCard from '@/pages/dashboard/components/MetricsCard';
import CardState from '@/pages/home/pages/studio/home/components/CardState';
import type { IMetricsDod } from '@/pages/dashboard/components/MetricsCardDod';
import type { ICardMetricItem } from '@/pages/dashboard/utils/kpiCardUtils';
import { formatFilterValues } from '@/pages/dashboard/utils/utils';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { dynamicString } from '@home/utils/dynamicString';
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';
import { getCountryFlag } from '@/pages/home/_config/countries';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import {
  mergeUaRoiUaMediaScopeIntoFormattedFilters,
  isUaRoiUaMediaScopeNoData,
  type TKpiOverviewDataScope,
} from '../../utils/uaRoiUaMediaScopeFilters';
import './index.scss';

const API = '/api/v1/dashboard/pc/overview_daily/card_metrics';
const DATA_KEY_BASIC = 'uaCardData';

// ─── 公共类型定义 ───────────────────────────────────────────────

/** 接口返回的卡片列表响应结构 */
interface ICardListResponse {
  card_list?: ICardMetricItem[];
}

/** 筛选器值对象：key → 数组或嵌套对象，运行时结构由筛选器组件决定 */
type TFilterVal = Record<string, unknown>;

/** selector 数据源：key → 可选项列表；列表项结构由后端决定 */
type TSelectorItem = Record<string, unknown>;
type TSelectorData = Record<string, TSelectorItem[]>;

/** DVA 根 state（仅使用到的字段） */
interface IRootState {
  dashboard?: { currentGame?: { game_code?: string; }; };
  dashboardSetting?: { selector?: TSelectorData; };
}

// ─── 数据格式化工具 ────────────────────────────────────────────

const mapCardListToMap = (data: ICardListResponse | undefined): Record<string, ICardMetricItem> => {
  const cardList: ICardMetricItem[] = data?.card_list || [];
  const map: Record<string, ICardMetricItem> = {};
  cardList.forEach(item => {
    map[item.metrics_name] = item;
  });
  return map;
};

const EMPTY_DOD: IMetricsDod = {
  titleEn: 'DoD', titleCn: '日环比',
  value: '', thisTime: '', thisValue: '', lastTime: '', lastValue: '',
  formatRule: 'percent1Decimal', hideBubbleContent: true,
};
const EMPTY_SDLW: IMetricsDod = {
  titleEn: 'SDLW', titleCn: '上周同期',
  value: '', thisTime: '', thisValue: '', lastTime: '', lastValue: '',
  formatRule: 'percent1Decimal', hideBubbleContent: true,
};

const EMPTY_CHANGE: IMetricsDod = {
  titleEn: 'Change', titleCn: '变化率',
  value: '', thisTime: '', thisValue: '', lastTime: '', lastValue: '',
  formatRule: 'percent1Decimal', hideBubbleContent: true,
};

interface IDodConfig {
  titleEn?: string;
  titleCn?: string;
  formatRule?: string;
  extendFormatRule?: string;
  hideBubbleContent?: boolean;
}

interface IDodDataConfig {
  dod?: IDodConfig;
  sdlw?: IDodConfig;
  change?: IDodConfig;
}

const buildDodData = (
  item: ICardMetricItem | undefined,
  cardFormatRule?: string,
  isChange?: boolean,
  dodDataConfig?: IDodDataConfig,
): IMetricsDod[] => {
  const extendFmt = cardFormatRule || 'thousandsInit';
  const defaultFmt = 'percent1Decimal';

  // 多天模式：只展示 change，不展示 dod/sdlw
  if (isChange) {
    const cfg = dodDataConfig?.change;
    const change: IMetricsDod = item?.change ? {
      titleEn: cfg?.titleEn || 'Change',
      titleCn: cfg?.titleCn || '变化率',
      value: item.change.show_value || '',
      thisTime: item.change.this_time || '',
      thisValue: item.change.this_value || '',
      lastTime: item.change.last_time || '',
      lastValue: item.change.last_value || '',
      formatRule: cfg?.formatRule || defaultFmt,
      extendFormatRule: cfg?.extendFormatRule || extendFmt,
      hideBubbleContent: cfg?.hideBubbleContent,
    } : EMPTY_CHANGE;
    return [change];
  }

  const dodCfg = dodDataConfig?.dod;
  const dod: IMetricsDod = item?.dod ? {
    titleEn: dodCfg?.titleEn || 'DoD',
    titleCn: dodCfg?.titleCn || '日环比',
    value: item.dod.show_value || '',
    thisTime: item.dod.this_time || '',
    thisValue: item.dod.this_value || '',
    lastTime: item.dod.last_time || '',
    lastValue: item.dod.last_value || '',
    formatRule: dodCfg?.formatRule || defaultFmt,
    extendFormatRule: dodCfg?.extendFormatRule || extendFmt,
    hideBubbleContent: dodCfg?.hideBubbleContent,
  } : EMPTY_DOD;

  const sdlwCfg = dodDataConfig?.sdlw;
  const sdlw: IMetricsDod = item?.sdlw ? {
    titleEn: sdlwCfg?.titleEn || 'SDLW',
    titleCn: sdlwCfg?.titleCn || '上周同期',
    value: item.sdlw.show_value || '',
    thisTime: item.sdlw.this_time || '',
    thisValue: item.sdlw.this_value || '',
    lastTime: item.sdlw.last_time || '',
    lastValue: item.sdlw.last_value || '',
    formatRule: sdlwCfg?.formatRule || defaultFmt,
    extendFormatRule: sdlwCfg?.extendFormatRule || extendFmt,
    hideBubbleContent: sdlwCfg?.hideBubbleContent,
  } : EMPTY_SDLW;

  return [dod, sdlw];
};

// ─── 通用筛选器覆盖解析 ─────────────────────────────────────────
//
// filterOverrides 配置项格式:
//   { filterKey: 'campaign', selectorKey: 'campaign', matchType: 'UA-ROI' }
//   { filterKey: 'ua_network', selectorKey: 'ua_network', matchContains: 'applovin' }
//   { filterKey: 'ua_network', selectorKey: 'ua_network', matchTypeNot: ['Organic', 'Pre-Install'] }
//   { filterKey: 'system', selectorKey: 'os_list', matchField: 'os_name_en', matchEquals: 'Android', valueField: 'os_id' }
//   { filterKey: 'market', values: ['us'] } / values: ['255'] — 与用户选择求交集，互斥则不请求

interface IFilterOverride {
  filterKey: string;
  selectorKey?: string;
  matchType?: string;
  matchTypeNot?: string[];
  matchContains?: string;
  matchField?: string;
  matchEquals?: string;
  valueField?: string;
  values?: string[];
}

/**
 * 从 selector 数据中解析 filterOverride 的默认值列表
 */
const resolveOverrideValues = (
  override: IFilterOverride,
  selector: TSelectorData | undefined,
): string[] => {
  if (override.values && override.values.length > 0) {
    return override.values;
  }
  if (!override.selectorKey || !selector) return [];
  const list: TSelectorItem[] = selector[override.selectorKey] || [];

  if (override.matchType) {
    return list
      .filter(item => item?.type === override.matchType)
      .map(item => item?.[override.valueField || 'code'] as string)
      .filter(Boolean);
  }
  if (override.matchTypeNot && override.matchTypeNot.length > 0) {
    const excludeSet = new Set(override.matchTypeNot);
    return list
      .filter(item => item?.type && !excludeSet.has(item.type as string))
      .map(item => item?.[override.valueField || 'code'] as string)
      .filter(Boolean);
  }
  if (override.matchContains) {
    const keyword = override.matchContains.toLowerCase();
    return list
      .filter(item => {
        const code = String(item?.[override.valueField || 'code'] || '').toLowerCase();
        const nameCh = String(item?.name_ch || '').toLowerCase();
        const nameEn = String(item?.name_en || '').toLowerCase();
        return code.includes(keyword) || nameCh.includes(keyword) || nameEn.includes(keyword);
      })
      .map(item => item?.[override.valueField || 'code'] as string)
      .filter(Boolean);
  }
  if (override.matchField && override.matchEquals !== undefined) {
    return list
      .filter(item => item?.[override.matchField!] === override.matchEquals)
      .map(item => item?.[override.valueField || 'code'] as string)
      .filter(Boolean);
  }
  return [];
};

/**
 * 从 filterVal 中提取某个 filterKey 的当前用户选择值
 * 支持级联筛选器（object with .type）和普通数组
 */
const getUserFilterValues = (filterVal: TFilterVal | undefined, filterKey: string): string[] => {
  if (filterKey === 'market') {
    // region 可能是对象 { markets: [...] }
    const region = filterVal?.region as { markets?: unknown; } | undefined;
    if (!region) return [];
    const markets = region?.markets;
    return Array.isArray(markets) ? (markets as string[]) : [];
  }
  const val = filterVal?.[filterKey];
  if (!val) return [];
  if (Array.isArray(val)) return val as string[];
  if (typeof val === 'object') {
    // 级联筛选结构：{ [filterKey]: [...] }
    const childVal = (val as Record<string, unknown>)[filterKey];
    if (Array.isArray(childVal)) return childVal as string[];
  }
  return [];
};

/**
 * 对用户筛选器值和覆盖默认值做交集。
 * 用户值为 ['255']（全选）时，使用覆盖默认值。
 * 否则取交集；交集为空返回 null 表示不需要请求。
 */
const intersectFilterValues = (
  userValues: string[],
  defaultValues: string[],
): string[] | null => {
  if (defaultValues.length === 0) return null;
  if (userValues.length === 0 || (userValues.length === 1 && userValues[0] === '255')) {
    return defaultValues;
  }
  const defaultSet = new Set(defaultValues);
  const result = userValues.filter(v => defaultSet.has(v));
  return result.length > 0 ? result : null;
};

/**
 * 当 override 配置了 matchTypeNot 时，检查用户筛选值是否包含被排除 type 的 code。
 * 若包含则返回 true，表示当前筛选条件与 override 互斥，不应发起请求。
 */
const hasExcludedTypeValues = (
  override: IFilterOverride,
  userValues: string[],
  selector: TSelectorData | undefined,
): boolean => {
  if (!override.matchTypeNot || override.matchTypeNot.length === 0) return false;
  if (userValues.length === 0 || (userValues.length === 1 && userValues[0] === '255')) return false;
  if (!override.selectorKey || !selector) return false;

  const list: TSelectorItem[] = selector[override.selectorKey] || [];
  const excludeSet = new Set(override.matchTypeNot);
  const excludedCodes = new Set(
    list
      .filter(item => item?.type && excludeSet.has(item.type as string))
      .map(item => String(item?.[override.valueField || 'code'] || ''))
      .filter(Boolean),
  );

  return userValues.some(v => excludedCodes.has(v));
};

/** 配置为 market + values: ['us'] 的 US 专用卡片 */
const isUsMarketOnlyOverride = (ov: IFilterOverride): boolean =>
  ov.filterKey === 'market' &&
  Array.isArray(ov.values) &&
  ov.values.length === 1 &&
  String(ov.values[0]).toLowerCase() === 'us';

/**
 * US 卡片：仅当用户国家为全选(255)或包含 us 时按 us 请求；否则不请求。
 * 与区域下具体国家列表做交集无关（例如区域选亚洲时仍可单独看美国北极星卡）。
 */
const intersectUsMarketCard = (userValues: string[]): string[] | null => {
  const normalized = userValues.map(v => String(v).toLowerCase());
  if (normalized.length === 0) {
    return ['us'];
  }
  if (normalized.includes('255')) {
    return ['us'];
  }
  if (normalized.includes('us')) {
    return ['us'];
  }
  return null;
};

// ─── 基础 Service 的 beforeRequest ──────────────────────────────

/** Service 传入的请求上下文（含基础 metrics 列表、gameCode、筛选值） */
interface IBasicReqData {
  basicMetrics?: string[];
  gameCode?: string;
  filterVal?: TFilterVal & {
    rangeTime?: string[];
    diff?: unknown;
    timeType?: unknown;
    singleDaily?: unknown;
  };
  kpiOverviewDataScope?: TKpiOverviewDataScope;
  selectorForUaScope?: TSelectorData;
  applyKpiUaRoiUaMediaScope?: boolean;
}

const basicBeforeRequest = (reqData: IBasicReqData): Record<string, unknown> | false => {
  const metrics: string[] = reqData?.basicMetrics || [];
  if (metrics.length === 0) return false;

  const f = reqData?.filterVal || {};
  const rt = f.rangeTime || [];
  const endDate = rt[1] || rt[0] || '';
  if (!endDate) return false;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { rangeTime: _rangeTime, diff: _diff, timeType: _timeType, singleDaily: _singleDaily, ...filters } = f;
  const isSingleDay = !rt[0] || !rt[1] || rt[0] === rt[1];
  const startDate = isSingleDay ? endDate : rt[0];

  let filtersPayload = formatFilterValues(filters) as Record<string, unknown>;
  if (
    reqData.applyKpiUaRoiUaMediaScope &&
    reqData.kpiOverviewDataScope === 'ua_roi_ua_media'
  ) {
    // 交集检查 + 注入 custom_filters 由 mergeUaRoiUaMediaScopeIntoFormattedFilters 统一处理
    const merged = mergeUaRoiUaMediaScopeIntoFormattedFilters(
      filtersPayload,
      reqData.filterVal as TFilterVal | undefined,
      reqData.selectorForUaScope,
    );
    if (merged === false) {
      return false;
    }
    filtersPayload = merged;
  }

  return {
    game_code: reqData.gameCode,
    period: 'daily',
    start_time: startDate,
    end_time: endDate,
    is_confirm: !isSingleDay,
    filters: filtersPayload,
    metrics,
  };
};

// ─── 日期相关配置说明（卡片 cfg）────────────────────────────────
// - dateShift：相对全局筛选 rangeTime 整体向前平移 N 天，用于独立请求的 start/end；
//   同时会作为 dateLabelShift 的默认来源，使日期标签与请求区间一致。
// - dateLabelShift：仅影响日期标签展示（不单独触发独立请求）；未配置时若存在 dateShift 则沿用其绝对值。
// - showDateLabel：显式 true 时始终展示日期行；显式 false 时不展示；未配置时仅当 dateLabelShift > 0 时展示。
// - showFilterSelectedDate：为 true 时展示筛选器当前选中的日期区间（与请求区间一致，无平移）；
//   优先级低于 showDateLabel / dateLabelShift（及 dateShift 衍生的 shift）：仅当不会进入高优先级展示分支时生效；
//   若 showDateLabel === false（显式关闭日期行），本项也不生效。

// ─── 构建带覆盖筛选的请求参数 ──────────────────────────────────

const FILTER_KEY_TO_API_KEY: Record<string, string> = {
  system: 'os_list',
  channel: 'channel_list',
  zone: 'zone_list',
  lang: 'lang_list',
  market: 'market_list',
};

const buildOverrideRequestParams = (
  filterVal: TFilterVal | undefined,
  gameCode: string,
  metrics: string[],
  resolvedOverrides: Array<{ filterKey: string; values: string[] | null; }>,
  dateShift: number | undefined,
  uaScopeOptions: {
    enabled: boolean;
    scope: TKpiOverviewDataScope;
    selector: TSelectorData | undefined;
  },
): Record<string, unknown> | null => {
  if (resolvedOverrides.some(o => o.values === null)) return null;

  const rt = (filterVal?.rangeTime as string[]) || [];
  const endDate = rt[1] || rt[0] || '';
  if (!endDate) return null;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { rangeTime: _rangeTime, diff: _diff, timeType: _timeType, singleDaily: _singleDaily, ...filters } = filterVal || {};
  const isSingleDay = !rt[0] || !rt[1] || rt[0] === rt[1];
  let startDate = isSingleDay ? endDate : rt[0];
  let adjustedEndDate = endDate;

  // 应用日期偏移
  if (dateShift) {
    startDate = moment(startDate).subtract(Math.abs(dateShift), 'days').format('YYYY-MM-DD');
    adjustedEndDate = moment(endDate).subtract(Math.abs(dateShift), 'days').format('YYYY-MM-DD');
  }

  // formatFilterValues 返回结构由后端约定，这里使用 Record 方便动态写入覆盖值
  let formatted = formatFilterValues(filters) as Record<string, unknown>;

  if (
    uaScopeOptions.enabled &&
    uaScopeOptions.scope === 'ua_roi_ua_media'
  ) {
    const scoped = mergeUaRoiUaMediaScopeIntoFormattedFilters(
      formatted,
      filterVal,
      uaScopeOptions.selector,
    );
    if (scoped === false) {
      return null;
    }
    formatted = scoped;
  }

  /** 后端约定的 custom_filters 数组项结构 */
  interface ICustomFilter {
    key: string;
    filter_list: string[];
  }

  resolvedOverrides.forEach(({ filterKey, values }) => {
    if (!values) return;
    const apiKey = FILTER_KEY_TO_API_KEY[filterKey];
    if (apiKey) {
      formatted[apiKey] = values;
    } else {
      const customFilters: ICustomFilter[] = (formatted?.custom_filters as ICustomFilter[]) || [];
      const idx = customFilters.findIndex((cf: ICustomFilter) => cf.key === filterKey);
      if (idx >= 0) {
        customFilters[idx] = { key: filterKey, filter_list: values };
      } else {
        customFilters.push({ key: filterKey, filter_list: values });
      }
      formatted.custom_filters = customFilters;
    }
  });

  return {
    game_code: gameCode,
    period: 'daily',
    start_time: startDate,
    end_time: adjustedEndDate,
    is_confirm: !isSingleDay,
    filters: formatted,
    metrics,
  };
};

// ─── 管理所有 override 卡片数据的 hook ──────────────────────────

/** 卡片配置：由 pageConfig 声明，字段较多且混合，运行时按需使用 */
interface IUaTeamCardConfig {
  /** 卡片绑定的指标 key；可使用 dynamicString 模板 */
  metricKey?: string;
  /** 覆盖式独立请求时的 metrics 列表；未配置时回退到 [metricKey] */
  overrideMetrics?: string[];
  /** 覆盖筛选条件 */
  filterOverrides?: IFilterOverride[];
  /** 日期相对整体筛选区间的平移天数 */
  dateShift?: number;
  /** 仅影响日期标签展示的偏移天数 */
  dateLabelShift?: number;
  /** 显式控制日期行显示 */
  showDateLabel?: boolean;
  /** 展示筛选器当前选中日期区间 */
  showFilterSelectedDate?: boolean;
  /** 展示用：数字格式化规则 */
  formatRule?: string;
  /** 卡片标题、单位、icon、tips 等（支持 dynamicString 模板） */
  titleCn?: string;
  titleEn?: string;
  subtitleCn?: string;
  subtitleEn?: string;
  preUnit?: string;
  endUnit?: string;
  icon?: string;
  imgUrl?: string;
  titleIcon?: string;
  tipsEn?: string;
  tipsCn?: string;
  /** 系统/国家标识图标列表 */
  iconImages?: Array<{ type: 'system' | 'country'; value: string; }>;
  /** 环比/同期文案覆盖配置 */
  dodData?: IDodDataConfig;
  /** 传入 MetricsCard 的样式类名 */
  className?: string;
  /** 允许承载其它业务扩展字段 */
  [key: string]: unknown;
  /** 强制走独立请求（无 filterOverrides/dateShift 时也走 useOverrideCardsData，使卡片有独立 loading） */
  forceIndependentRequest?: boolean;
  /** UA-ROI & UA Media 口径 Tab 下隐藏该卡片（如 UA-ROI Spend 卡） */
  omitInUaRoiUaMediaScopeTab?: boolean;
}

interface IOverrideCardState {
  data: Record<string, ICardMetricItem> | null;
  loading: boolean;
  error: boolean;
}

type TOverrideDataMap = Record<number, IOverrideCardState>;

const useOverrideCardsData = (
  cards: IUaTeamCardConfig[],
  filterVal: TFilterVal | undefined,
  filterLoading: boolean,
  gameCode: string,
  selector: TSelectorData | undefined,
  uaScopeOptions: {
    enabled: boolean;
    scope: TKpiOverviewDataScope;
  },
): TOverrideDataMap => {
  const [dataMap, setDataMap] = useState<TOverrideDataMap>({});
  // 每张卡片独立的 inflight 计数器，用于防竞态
  const inflightMapRef = useRef<Record<number, number>>({});

  const overrideCardsInfo = useMemo(() => {
    if (!Array.isArray(cards)) return [];
    return cards
      .map((cfg: IUaTeamCardConfig, idx: number) => {
        const overrides: IFilterOverride[] | undefined = cfg.filterOverrides;
        const hasOverrides = Array.isArray(overrides) && overrides.length > 0;
        const {dateShift} = cfg;
        // 有 filterOverrides、dateShift 或 forceIndependentRequest 的卡片都需要独立请求
        if (!hasOverrides && !dateShift && !cfg.forceIndependentRequest) return null;
        const metrics: string[] = cfg.overrideMetrics || [cfg.metricKey].filter(Boolean) as string[];
        return { idx, overrides: hasOverrides ? overrides! : [], metrics, dateShift };
      })
      .filter(Boolean) as Array<{ idx: number; overrides: IFilterOverride[]; metrics: string[]; dateShift?: number; }>;
  }, [cards]);

  const resolvedParams = useMemo(() => {
    if (!selector || !gameCode || filterLoading) return null;
    return overrideCardsInfo.map(({ idx, overrides, metrics, dateShift }) => {
      const resolved = overrides.map(ov => {
        const defaultValues = resolveOverrideValues(ov, selector);
        const userValues = getUserFilterValues(filterVal, ov.filterKey);

        // matchTypeNot：用户选了被排除 type 的值时，直接互斥不请求
        if (hasExcludedTypeValues(ov, userValues, selector)) {
          return { filterKey: ov.filterKey, values: null };
        }

        const values = isUsMarketOnlyOverride(ov)
          ? intersectUsMarketCard(userValues)
          : intersectFilterValues(userValues, defaultValues);
        return { filterKey: ov.filterKey, values };
      });
      const params = buildOverrideRequestParams(
        filterVal,
        gameCode,
        metrics,
        resolved,
        dateShift,
        { enabled: uaScopeOptions.enabled, scope: uaScopeOptions.scope, selector },
      );
      return { idx, params };
    });
  }, [
    overrideCardsInfo,
    selector,
    gameCode,
    filterVal,
    filterLoading,
    uaScopeOptions.enabled,
    uaScopeOptions.scope,
  ]);

  useEffect(() => {
    if (!resolvedParams || filterLoading || !gameCode) return;

    resolvedParams.forEach(({ idx, params }) => {
      // 为每张卡片递增独立的 inflight 计数
      if (!inflightMapRef.current[idx]) {
        inflightMapRef.current[idx] = 0;
      }
      inflightMapRef.current[idx] += 1;
      const batch = inflightMapRef.current[idx];

      if (!params) {
        // 互斥条件不满足，不需要请求
        setDataMap(prev => ({
          ...prev,
          [idx]: { data: null, loading: false, error: false },
        }));
        return;
      }

      // 先置为 loading
      setDataMap(prev => ({
        ...prev,
        [idx]: { data: prev[idx]?.data || null, loading: true, error: false },
      }));

      dashboardRequest({ url: API, method: 'post', data: params })
        .then((res) => {
          if (batch !== inflightMapRef.current[idx]) return;
          setDataMap(prev => ({
            ...prev,
            // dashboardRequest 返回 unknown，按接口约定断言为 ICardListResponse
            [idx]: { data: mapCardListToMap(res as ICardListResponse), loading: false, error: false },
          }));
        })
        .catch(() => {
          if (batch !== inflightMapRef.current[idx]) return;
          setDataMap(prev => ({
            ...prev,
            [idx]: { data: null, loading: false, error: true },
          }));
        });
    });
  }, [resolvedParams, filterLoading, gameCode]);

  return dataMap;
};

// ─── 渲染 Grid ──────────────────────────────────────────────────

/** Service 层向下派发的数据结构（按 dataKey 分组，含 .data 字段） */
type TServiceData = Record<string, unknown> & {
  [DATA_KEY_BASIC]?: { data?: Record<string, ICardMetricItem>; };
};

interface IUaTeamMetricsCardsGridProps {
  data?: TServiceData;
  cards: IUaTeamCardConfig[];
  colCount?: number;
  /** 自定义每行卡片数量，如 [2, 3] 表示第一行 2 张、第二行 3 张；优先级高于 colCount */
  rowLayout?: number[];
  overrideDataMap?: TOverrideDataMap;
  isChange?: boolean;
  /** 基础卡片应展示无数据（beforeRequest 返回 false 时旧数据仍在 state 中） */
  basicNoData?: boolean;
  computeDateLabel: (shift: number) => string;
  className?: string;
  style?: React.CSSProperties;
}

const UaTeamMetricsCardsGrid = (props: IUaTeamMetricsCardsGridProps) => {
  const { data: serviceData, cards, colCount = 3, rowLayout, overrideDataMap, isChange, basicNoData, computeDateLabel, className, style } = props;
  const basicMap = serviceData?.[DATA_KEY_BASIC]?.data;
  const rows: IUaTeamCardConfig[][] = useMemo(() => {
    if (!Array.isArray(cards)) return [];
    const result: IUaTeamCardConfig[][] = [];

    // 仅保留正整数，避免 0/负数导致死循环或空行
    const safeRowLayout = Array.isArray(rowLayout)
      ? rowLayout.filter(n => Number.isInteger(n) && n > 0)
      : [];

    if (safeRowLayout.length > 0) {
      // 按自定义行布局切分
      let offset = 0;
      for (let r = 0;r < safeRowLayout.length && offset < cards.length;r++) {
        const count = safeRowLayout[r];
        result.push(cards.slice(offset, offset + count));
        offset += count;
      }
      // 剩余卡片按最后一行的 count 继续切分（lastCount 已确保 > 0，不会死循环）
      const lastCount = safeRowLayout[safeRowLayout.length - 1];
      while (offset < cards.length) {
        result.push(cards.slice(offset, offset + lastCount));
        offset += lastCount;
      }
    } else {
      for (let i = 0;i < cards.length;i += colCount) {
        result.push(cards.slice(i, i + colCount));
      }
    }

    return result;
  }, [cards, colCount, rowLayout]);

  const resolveIconImages = (iconImages: Array<{ type: 'system' | 'country'; value: string; }> | undefined) => {
    if (!Array.isArray(iconImages) || iconImages.length === 0) return null;
    return (
      <span className="dbd-ua-team-metrics-cards__icon-images">
        {iconImages.map((img, i: number) => {
          let src = '';
          if (img.type === 'system') {
            src = getSystemFlag(img.value) || '';
          } else if (img.type === 'country') {
            src = getCountryFlag(img.value) || '';
          }
          return src ? <img key={i} className="dbd-ua-team-metrics-cards__icon-img" src={src} alt="" /> : null;
        })}
      </span>
    );
  };

  const resolveCard = (cfg: IUaTeamCardConfig, cardIdx: number) => {
    const hasFilterOverrides = Array.isArray(cfg.filterOverrides) && cfg.filterOverrides.length > 0;
    // 独立请求由 filterOverrides、dateShift 或 forceIndependentRequest 决定
    const hasRequestOverride = hasFilterOverrides || !!cfg.dateShift || !!cfg.forceIndependentRequest;
    const overrideState = hasRequestOverride ? overrideDataMap?.[cardIdx] : undefined;
    let source: Record<string, ICardMetricItem> | undefined;
    if (hasRequestOverride) {
      source = overrideState?.data || {};
    } else if (!basicNoData) {
      source = basicMap;
    }
    const item: ICardMetricItem | undefined = source?.[dynamicString(cfg.metricKey || '', serviceData)];
    const fmtRule = dynamicString(cfg.formatRule || '', serviceData) || cfg.formatRule;
    const iconImagesNode = resolveIconImages(cfg.iconImages);

    let dateLabelShift = 0;
    if (Number.isFinite(cfg.dateLabelShift)) {
      dateLabelShift = Math.abs(Number(cfg.dateLabelShift));
    } else if (cfg.dateShift) {
      dateLabelShift = Math.abs(cfg.dateShift);
    }
    const filterSelectedRangeLabel = computeDateLabel(0);
    const shiftedRangeLabel =
      dateLabelShift > 0 ? computeDateLabel(dateLabelShift) : '';

    /** 高优先级：与 dateLabelShift / showDateLabel / dateShift 衍生展示一致 */
    const shouldShowDatePrimary =
      cfg.showDateLabel === true ||
      (cfg.showDateLabel !== false && dateLabelShift > 0);

    let shouldShowDateRow = shouldShowDatePrimary;
    let dateLabelText = '';
    if (shouldShowDatePrimary) {
      dateLabelText =
        dateLabelShift > 0 ? shiftedRangeLabel : filterSelectedRangeLabel;
    } else if (
      cfg.showFilterSelectedDate === true &&
      cfg.showDateLabel !== false
    ) {
      shouldShowDateRow = true;
      dateLabelText = filterSelectedRangeLabel;
    }

    // 有数据时展示实际日期，无数据展示占位符
    const hasData = hasRequestOverride ? (overrideState?.data && item?.value) : !!item?.value;
    const dateLabelNode = shouldShowDateRow ? (
      <div className="dbd-ua-team-metrics-cards__date-label">
        {hasData ? dateLabelText : '-'}
      </div>
    ) : (
      <div className="dbd-ua-team-metrics-cards__date-label" style={{ visibility: 'hidden' }}>
        &nbsp;
      </div>
    );

    return {
      titleCn: dynamicString(cfg.titleCn || '', serviceData) || cfg.titleCn,
      titleEn: dynamicString(cfg.titleEn || '', serviceData) || cfg.titleEn,
      subtitleCn: cfg.subtitleCn ? dynamicString(cfg.subtitleCn, serviceData) : undefined,
      subtitleEn: cfg.subtitleEn ? dynamicString(cfg.subtitleEn, serviceData) : undefined,
      value: item?.value || '',
      formatRule: fmtRule,
      preUnit: cfg.preUnit ? dynamicString(cfg.preUnit, serviceData) : undefined,
      endUnit: cfg.endUnit ? dynamicString(cfg.endUnit, serviceData) : undefined,
      icon: iconImagesNode ? undefined : cfg.icon,
      imgUrl: iconImagesNode ? undefined : cfg.imgUrl,
      titleIcon: cfg.titleIcon,
      tipsEn: cfg.tipsEn ? dynamicString(cfg.tipsEn, serviceData) : undefined,
      tipsCn: cfg.tipsCn ? dynamicString(cfg.tipsCn, serviceData) : undefined,
      dodData: buildDodData(item, fmtRule, isChange, cfg.dodData),
      iconImagesNode,
      contentHeader: dateLabelNode,
      // override 卡片独立的加载/错误状态
      isOverride: hasRequestOverride,
      isOverrideLoading: hasRequestOverride && (overrideState?.loading !== false),
      isOverrideError: hasRequestOverride && (overrideState?.error === true),
    };
  };

  let globalIdx = -1;
  return (
    <div className={`dbd-ua-team-metrics-cards ${className}`} style={style}>
      {rows.map((row, rowIdx) => (
        <div key={rowIdx} className="dbd-ua-team-metrics-cards__row">
          {row.map((cfg: IUaTeamCardConfig, colIdx: number) => {
            globalIdx += 1;
            const cardIdx = globalIdx;
            const card = resolveCard(cfg, cardIdx);

            const metricsCardNode = (
              <MetricsCard
                // MetricsCard 声明 titleCn/titleEn 为 string，这里可能传入 <span>（包裹 icon+文本）；
                // React 运行时能正常渲染，类型层面使用 as unknown as string 对齐
                titleCn={(card.iconImagesNode
                  ? <span title={String(card.titleCn)}>{card.iconImagesNode}{card.titleCn}</span>
                  : card.titleCn) as unknown as string}
                titleEn={(card.iconImagesNode
                  ? <span title={String(card.titleEn)}>{card.iconImagesNode}{card.titleEn}</span>
                  : card.titleEn) as unknown as string}
                subtitleCn={card.subtitleCn}
                subtitleEn={card.subtitleEn}
                contentHeader={card.contentHeader}
                value={card.value}
                formatRule={card.formatRule}
                preUnit={card.preUnit}
                endUnit={card.endUnit}
                icon={card.icon}
                imgUrl={card.imgUrl}
                titleIcon={card.titleIcon}
                tipsEn={card.tipsEn}
                tipsCn={card.tipsCn}
                dodData={card.dodData}
                className={cfg.className}
              />
            );

            // override 卡片各自独立 loading / error
            if (card.isOverride) {
              return (
                <div key={`${rowIdx}-${colIdx}`} className="dbd-ua-team-metrics-cards__col">
                  <Abnormal
                    isLoading={card.isOverrideLoading}
                    isError={card.isOverrideError}
                    style={{ height: 170, background: '#fff' }}
                    component={metricsCardNode}
                  />
                </div>
              );
            }

            return (
              <div key={`${rowIdx}-${colIdx}`} className="dbd-ua-team-metrics-cards__col">
                {metricsCardNode}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ─── 主组件 ─────────────────────────────────────────────────────

/**
 * UaTeamMetricsCards
 * 是否启用「UA-ROI & UA Media」口径行为完全由父级 KpiOverviewScopeTabs 注入的
 * `kpiOverviewDataScope` 上下文驱动（取自 props.data.kpiOverviewDataScope 或 props.kpiOverviewDataScope）；
 * 不再受组件 props 上的 applyKpiUaRoiUaMediaScope 控制。
 */
interface IUaTeamMetricsCardsProps {
  /** 父级 Service 注入的数据（pageConfig 风格，按 dataKey 聚合） */
  data?: TServiceData;
  /** pageConfig 层注入的额外配置对象；可能包含 props 用于外层合并 */
  config?: { props?: Partial<IUaTeamMetricsCardsProps>; };
  className?: string;
  style?: React.CSSProperties;
  cards?: IUaTeamCardConfig[];
  /** UA scope tab 下使用的独立卡片配置；未配置时回退到 cards（通过 omitInUaRoiUaMediaScopeTab 过滤） */
  uaScopeCards?: IUaTeamCardConfig[];
  colCount?: number;
  /** UA scope tab 下自定义每行卡片数量，如 [2, 3] 表示上面 2 个、下面 3 个 */
  uaScopeRowLayout?: number[];
  /** 可选覆盖；默认取自 props.data.kpiOverviewDataScope（KpiOverviewScopeTabs 下发） */
  kpiOverviewDataScope?: TKpiOverviewDataScope;
}

const UaTeamMetricsCards: React.FC<IUaTeamMetricsCardsProps> = (props) => {
  const merged = { ...props, ...(props.config?.props || {}) };
  const {
    data: parentData,
    cards = [],
    uaScopeCards,
    colCount = 3,
    uaScopeRowLayout,
    className,
    style,
    kpiOverviewDataScope: kpiOverviewDataScopeProp,
  } = merged;

  const kpiOverviewDataScope =
    kpiOverviewDataScopeProp ??
    (parentData as { kpiOverviewDataScope?: TKpiOverviewDataScope; } | undefined)
      ?.kpiOverviewDataScope ??
    'all';

  const isUaScopeTab = kpiOverviewDataScope === 'ua_roi_ua_media';

  const displayCards = useMemo((): IUaTeamCardConfig[] => {
    if (!isUaScopeTab) {
      return cards;
    }
    // 优先使用独立的 uaScopeCards 配置，否则回退到 cards 过滤
    if (Array.isArray(uaScopeCards) && uaScopeCards.length > 0) {
      return uaScopeCards;
    }
    return cards.filter(c => !c.omitInUaRoiUaMediaScopeTab);
  }, [isUaScopeTab, cards, uaScopeCards]);

  const { gameCode, selector } = useSelector(
    (state: IRootState) => ({
      gameCode: state?.dashboard?.currentGame?.game_code,
      selector: state?.dashboardSetting?.selector,
    }),
    shallowEqual,
  );

  const { filterVal, filterLoading } = useContext(FilterContext);

  // 判断是否多天模式：多天时只展示 change，不展示 dod/sdlw
  const isChange = useMemo(() => {
    const rt = filterVal?.rangeTime || [];
    // Boolean 包裹以返回严格布尔类型，语义与原逻辑等价
    return Boolean(rt[0] && rt[1] && rt[0] !== rt[1]);
  }, [filterVal?.rangeTime]);

  // 根据偏移天数计算日期标签
  const computeDateLabel = useCallback((shift: number) => {
    const rt = filterVal?.rangeTime || [];
    const startDate = rt[0] || '';
    const endDate = rt[1] || rt[0] || '';
    if (!endDate) return '';
    const isSingleDay = !startDate || startDate === endDate;
    const start = moment(isSingleDay ? endDate : startDate).subtract(shift, 'days').format('YYYY-MM-DD');
    const end = moment(endDate).subtract(shift, 'days').format('YYYY-MM-DD');
    if (start === end) return start;
    return `${start} ~ ${end}`;
  }, [filterVal?.rangeTime]);

  const basicMetrics = useMemo(() => {
    if (!Array.isArray(displayCards)) return [];
    const set = new Set<string>();
    displayCards.forEach((c: IUaTeamCardConfig) => {
      const hasFilterOverrides = Array.isArray(c.filterOverrides) && c.filterOverrides.length > 0;
      // 有 filterOverrides、dateShift 或 forceIndependentRequest 的卡片走独立请求，不纳入基础请求
      if (!hasFilterOverrides && !c.dateShift && !c.forceIndependentRequest) {
        if (c.metricKey) set.add(c.metricKey);
      }
    });
    return [...set];
  }, [displayCards]);

  const serviceData = useMemo(
    () => ({
      ...parentData,
      gameCode,
      basicMetrics,
      kpiOverviewDataScope,
      selectorForUaScope: selector,
      applyKpiUaRoiUaMediaScope: isUaScopeTab,
    }),
    [
      parentData,
      gameCode,
      basicMetrics,
      kpiOverviewDataScope,
      selector,
      isUaScopeTab,
    ],
  );

  const hasBasicCards = basicMetrics.length > 0;

  const overrideDataMap = useOverrideCardsData(
    displayCards,
    filterVal,
    filterLoading,
    gameCode || '',
    selector,
    {
      enabled: isUaScopeTab,
      scope: kpiOverviewDataScope,
    },
  );

  const activeRowLayout = useMemo(() => {
    if (isUaScopeTab && uaScopeRowLayout) {
      return uaScopeRowLayout;
    }
    return undefined;
  }, [isUaScopeTab, uaScopeRowLayout]);

  // UA scope tab 下，若用户筛选与 UA-ROI/UA 交集为空，基础卡片应展示空数据
  const basicNoData = useMemo(() => {
    if (!isUaScopeTab) return false;
    return isUaRoiUaMediaScopeNoData(filterVal, selector);
  }, [isUaScopeTab, filterVal, selector]);

  // 有基础卡片时，data 由外层 Service 注入，组件层透传 undefined 即可；
  // 无基础卡片时（纯 override 卡片场景），无 Service 包裹，需要把 parentData 直接透传给 grid，
  // 以便 grid 内部 dynamicString(metricKey, serviceData) 能解析模板。
  const gridData = hasBasicCards ? undefined : (parentData as TServiceData);

  const grid = (
    <UaTeamMetricsCardsGrid
      data={gridData}
      cards={displayCards}
      colCount={colCount}
      rowLayout={activeRowLayout}
      overrideDataMap={overrideDataMap}
      isChange={isChange}
      basicNoData={basicNoData}
      computeDateLabel={computeDateLabel}
      className={className}
      style={style}
    />
  );

  if (!hasBasicCards) {
    return grid;
  }

  return (
    <Service
      api={API}
      data={serviceData}
      dataKey={DATA_KEY_BASIC}
      trackData={[
        'gameCode',
        'basicMetrics',
        'kpiOverviewDataScope',
        'selectorForUaScope',
        'applyKpiUaRoiUaMediaScope',
      ]}
      beforeRequest={basicBeforeRequest}
      formatData={mapCardListToMap}
    >
      <CardState
        apiGroup={[DATA_KEY_BASIC]}
        style={{ minHeight: 200, marginTop: 20, marginBottom: 10 }}
      >
        {grid}
      </CardState>
    </Service>
  );
};

export default UaTeamMetricsCards;
